import {
  Button,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
} from "@heroui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  ExternalLink,
  FilePlus2,
  FileText,
  QrCode,
  ScanText,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";

import FileDropZone from "@/components/common/FileDropZone";
import { errorMessage } from "@/lib/api";
import { showSuccess } from "@/lib/toast";
import {
  deleteDrawingRevision,
  getDrawingRevisionUrl,
  listDrawingRevisions,
  openDrawingRevision,
  thumbnailSrc,
  uploadDrawingRevision,
} from "../api";
import type { OcrConfidence } from "../api";
import QrAreaDialog from "./QrAreaDialog";
import type { DrawingQrStamp, PanelDrawing, PanelItem, QrStampArea } from "./types";

/** 読み取りの確度の文言。カードと同じ言い方に揃える。 */
const confidenceLabel: Record<OcrConfidence, string> = {
  high: "OCR確度 高",
  medium: "OCR確度 中（2 位の候補が近いので目で確かめてください）",
  low: "OCR確度 低（決められなかったので図番には入れていません）",
};

/**
 * カードを押して開くダイアログ。**読み取った図番を直し、紐付け先の明細を選ぶ**。
 *
 * 一覧のカードには絵と図番と状態しか出していないので、直す操作はここに集約する。
 * 図番は突き合わせの鍵で、OCR（今はファイル名からの推測）が外れたときにここで
 * 直す。直したあとに突き合わせ直すか、その場で明細を選ぶかは利用者に任せる。
 *
 * 絵は一覧と同じサムネイルを大きく出す。**押すと PDF が別タブで開く**
 * （[[Thumbnail]]）。埋め込みビューアをここに置かないのは、ダイアログの中で図面を
 * 読む形になって狭いため。
 *
 * 紐付け先が無いときは、**その図番で明細を起こしてそのまま紐付ける**導線も出す。
 * 依頼書に載っていない図面が届くことがあり、選択肢の中に行が無いため。
 *
 * 間に**現場へ出す QR**の欄がある（案件の画面だけ。[[QrSection]]）。押すと QR 付き
 * の版が 1 つ増え、以後は一覧の絵も「PDF を開く」もそちらを指す。
 *
 * 下半分は**版（図面 PDF）の差し替えと履歴**。PDF を 1 枚落とすと新しい版として
 * 登録され、**版番号はサーバーが採番する**（登録済みの数字の版の最大 + 1）。
 * 画面から版を打たせないのは、差し替えのたびに採番の規則を思い出すことになり、
 * 打ち間違えると同じ版が二重になって登録できなくなるため。
 *
 * **前の版は消さずに残る**。どの版で見積ったのかを後から辿れるようにするため。
 *
 * 版の差し替えは、見積から引き継いだ図面（``can_relink`` が false）でもできる。
 * 止めているのは紐付けと削除で、あれは**どの明細の図面か**が見積 → 案件の一方向
 * で決まるから。PDF そのものは同じ 1 枚の実体で、現場に新しい図面が届いたのなら
 * 見積側にも新しい版が見えてよい。
 */
export default function DrawingDetailDialog({
  drawing,
  items,
  canWrite,
  linkHint,
  createItemHint,
  onClose,
  onSaveNumber,
  onLink,
  onCreateItem,
  onDelete,
  onRevisionsChanged,
  onStampQr,
}: {
  /** null のとき閉じている。 */
  drawing: PanelDrawing | null;
  /**
   * 紐付け先の候補（製作対象の明細のうち、**まだ図面が付いていない行**）。
   *
   * 既に図面が付いている行を候補に残すと、選んだ瞬間に前の図面が外れる。
   * 1 つの明細に 2 枚の図面を付けることは無いので、選ばせないほうが早い。
   * 付け替えたいときは、先に相手の図面の紐付けを外す。
   */
  items: PanelItem[];
  canWrite: boolean;
  /** 紐付け先の選択欄の下に出す補足（候補の決め方は持ち主で違う）。 */
  linkHint: string;
  /** 「この図番で明細を作って紐付ける」の補足。 */
  createItemHint: string;
  onClose: () => void;
  /** 図番を保存する。エラー文言を返すと閉じずに表示する。 */
  onSaveNumber: (
    drawing: PanelDrawing,
    drawingNumber: string,
  ) => Promise<string | null>;
  onLink: (drawing: PanelDrawing, itemId: number | null) => void;
  /**
   * 図番で明細を起こして紐付ける。入力中の図番を先に保存してから呼ぶので、
   * 保存と作成の 2 つを押させない。エラー文言を返すと閉じずに表示する。
   */
  onCreateItem: (
    drawing: PanelDrawing,
    drawingNumber: string,
  ) => Promise<string | null>;
  onDelete: (drawing: PanelDrawing) => void;
  /** 版が増減したときに呼ぶ（一覧のサムネイル・版数を引き直すため）。 */
  onRevisionsChanged: () => void;
  /**
   * QR を押印する。**省くと欄ごと出さない**（見積の画面では押せない）。
   */
  onStampQr?: (
    drawingId: number,
    area: QrStampArea,
    scalePercent: number,
  ) => Promise<DrawingQrStamp>;
}) {
  const [drawingNumber, setDrawingNumber] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);

  // 開くたびに対象の値を入れ直す（前回の入力を持ち越さない）。
  useEffect(() => {
    if (!drawing) return;
    // 未読み取りは null。入力欄は空文字で扱う。
    setDrawingNumber(drawing.drawing_number ?? "");
    setError(null);
    setSaving(false);
    setCreating(false);
  }, [drawing]);

  if (!drawing) return null;

  /**
   * 開いたときから変わっているか。**変わっていなければ ✔ を出さない**。
   * 押しても何も起きないボタンを置くと、押したのに閉じないのを不具合と読まれる。
   */
  const dirty = drawingNumber.trim() !== (drawing.drawing_number ?? "");

  /** 図番だけを保存する。**ダイアログは閉じない**。 */
  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    const message = await onSaveNumber(drawing, drawingNumber.trim());
    setSaving(false);
    if (message) {
      setError(message);
      return;
    }
    // 保存できると `drawing` が取り直されて欄の値と揃うので、✔ はひとりでに
    // 消える（＝直すところが無くなったことが見た目で分かる）。
    showSuccess("図番を保存しました。");
  };

  const createItem = async () => {
    setCreating(true);
    const message = await onCreateItem(drawing, drawingNumber.trim());
    setCreating(false);
    if (message) {
      setError(message);
      return;
    }
    onClose();
  };

  const openPdf = async () => {
    if (drawing.latest_revision_id == null) return;
    try {
      const url = await getDrawingRevisionUrl(
        drawing.drawing_id,
        drawing.latest_revision_id,
      );
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(errorMessage(e, "PDF を開けませんでした。"));
    }
  };

  return (
    <Modal
      isOpen
      onOpenChange={(open) => !open && onClose()}
      size="3xl"
      // 版が増えると縦に伸びるので、ダイアログの中でスクロールさせる。
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>図面の確認</span>
          <span className="text-small font-normal text-default-500">
            {drawing.can_edit_number || drawing.can_relink
              ? "読み取った図番を直し、紐付け先の明細を選びます。"
              : // 見積から引き継いだ図面。ここでできるのは確かめることと、
                // 版の差し替え・QR の押印だけ。
                "図面を確かめます。図番と紐付けは見積の側で直します。"}
          </span>
        </ModalHeader>

        <ModalBody>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Thumbnail
              src={thumbnailSrc(drawing.thumbnail_path)}
              // PDF が 1 枚も無ければ開けない（絵だけ残っていることはない）。
              onOpen={drawing.latest_revision_id == null ? undefined : openPdf}
            />

            <div className="flex flex-col gap-4">
              <Input
                label="図番"
                labelPlacement="outside"
                placeholder=" "
                aria-label="図番"
                variant="bordered"
                radius="sm"
                value={drawingNumber}
                isReadOnly={!canWrite || !drawing.can_edit_number}
                description={
                  drawing.can_edit_number
                    ? "突き合わせの鍵です。読み取りが違っていればここで直します。"
                    : // 直せない相手（見積から引き継いだ図面）には、どこで直すのか
                      // を書く。灰色の欄だけでは「権限が無い」と読める。
                      "見積から引き継いだ図面です。図番は見積の図面タブで直します。"
                }
                onValueChange={(v) => {
                  setDrawingNumber(v);
                  setError(null);
                }}
                // 入力欄の中で確定できるようにする。欄を離れた位置にボタンを置くと、
                // どの項目を保存するのかが読み取りにくい。
                endContent={
                  canWrite &&
                  drawing.can_edit_number && (
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      color="primary"
                      aria-label="図番を保存"
                      data-testid="drawing-save"
                      // 変えていない間は押せない（押しても何も起きないため）。
                      isDisabled={!dirty}
                      isLoading={saving}
                      onPress={() => void save()}
                    >
                      <Check size={18} />
                    </Button>
                  )
                }
                // 1 項目だけの欄なので Enter で確定できる（表のセルと同じ）。
                onKeyDown={(e) => {
                  if (e.key === "Enter" && drawing.can_edit_number) {
                    e.preventDefault();
                    void save();
                  }
                }}
              />

              {/* OCR が読んだ値が今の図番と違うときだけ出す。**どちらが正しいかは
                  機械では決められない**ので、置き換えるかどうかは人が決める。
                  図番が空のときは自動で入っているので、ここには出てこない。 */}
              {drawing.ocr_drawing_number &&
                drawing.ocr_drawing_number !== drawingNumber && (
                  <div className="flex items-center gap-2 rounded-md bg-default-50 px-3 py-2">
                    <ScanText size={16} className="shrink-0 text-default-400" />
                    <span className="min-w-0 flex-1 text-sm break-all text-default-600">
                      OCR は{" "}
                      <span className="font-bold">{drawing.ocr_drawing_number}</span>{" "}
                      と読みました
                      {/* 一覧には出さない根拠の数値をここだけで出す。確度を
                          疑ったときに「どれくらいの差だったのか」を確かめられる
                          ようにしておく。 */}
                      {drawing.ocr_confidence && (
                        <span className="block text-tiny text-default-400">
                          {confidenceLabel[drawing.ocr_confidence]}
                          {drawing.ocr_margin != null &&
                            `（2 位との差 ${drawing.ocr_margin.toFixed(2)}）`}
                        </span>
                      )}
                    </span>
                    {canWrite && (
                      <Button
                        size="sm"
                        radius="sm"
                        variant="flat"
                        className="shrink-0"
                        onPress={() => {
                          setDrawingNumber(drawing.ocr_drawing_number ?? "");
                          setError(null);
                        }}
                      >
                        この値を使う
                      </Button>
                    )}
                  </div>
                )}

              {/* description は付けない。HeroUI の Select は description を
                  渡すと labelPlacement="outside" のラベルが絶対配置を失い、
                  コントロールに重なる（実際に測って確かめてある）。補足は下に
                  素のテキストで置く。 */}
              <Select
                label="紐付け先の明細"
                labelPlacement="outside"
                placeholder="選択してください"
                aria-label="紐付け先の明細"
                variant="bordered"
                radius="sm"
                isDisabled={!canWrite || !drawing.can_relink}
                selectedKeys={drawing.item_id == null ? [] : [String(drawing.item_id)]}
                onSelectionChange={(keys) => {
                  const key = [...keys][0];
                  onLink(drawing, key == null ? null : Number(key));
                }}
              >
                {items.map((item) => (
                  <SelectItem key={String(item.item_id)}>
                    {`${item.sequence} 行目 ${item.drawing_number ?? ""} ${item.item_name}`}
                  </SelectItem>
                ))}
              </Select>
              <p className="-mt-2 text-tiny text-default-500">{linkHint}</p>

              {/* 候補の中に行が無いことがある（依頼書に載っていない図面）。
                  その場で行を起こせるようにしておかないと、明細タブへ移って
                  図番を打ち直し、戻ってきて選ぶことになる。 */}
              {canWrite && drawing.item_id == null && drawing.can_relink && (
                <div className="flex flex-col gap-1">
                  <Button
                    size="sm"
                    radius="full"
                    variant="bordered"
                    color="primary"
                    className="self-start"
                    data-testid="drawing-create-item"
                    startContent={<FilePlus2 size={14} />}
                    isDisabled={!drawingNumber.trim()}
                    isLoading={creating}
                    onPress={() => void createItem()}
                  >
                    この図番で明細を作って紐付ける
                  </Button>
                  <span className="text-tiny text-default-500">
                    {drawingNumber.trim() ? createItemHint : "図番を入れると使えます。"}
                  </span>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {canWrite && drawing.can_delete && (
                  <Button
                    size="sm"
                    radius="full"
                    variant="light"
                    color="danger"
                    startContent={<Trash2 size={14} />}
                    onPress={() => onDelete(drawing)}
                  >
                    この図面を削除
                  </Button>
                )}
              </div>

              {drawing.origin_note && !drawing.can_relink && (
                <p className="rounded-md bg-default-50 px-3 py-2 text-tiny text-default-600">
                  {drawing.origin_note}。紐付けと削除は出どころの画面で行ってください
                  （こちらで変えても、あちらの操作で戻ってしまうためです）。
                </p>
              )}

              {drawing.created_at && (
                <p className="text-tiny text-default-500">
                  取込 {drawing.created_at.slice(0, 10)}
                </p>
              )}
            </div>
          </div>

          {onStampQr && (
            <QrSection
              drawing={drawing}
              canWrite={canWrite}
              onStampQr={onStampQr}
              onChanged={onRevisionsChanged}
              onError={setError}
            />
          )}

          <RevisionSection
            drawingId={drawing.drawing_id}
            canWrite={canWrite}
            onChanged={onRevisionsChanged}
            onError={setError}
          />

          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>

        <ModalFooter>
          {/* 図番は欄の中の ✔ で確定する。保存のボタンをフッタにも置くと、
              どちらを押せばよいのか分からなくなる。 */}
          <Button variant="light" radius="full" data-testid="cancel" onPress={onClose}>
            閉じる
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/**
 * 図面のサムネイル。**押すと PDF が別タブで開く**。
 *
 * 絵が出ていると、まずそこを押したくなる。以前は横に「PDF を開く」ボタンを並べて
 * いたが、絵が押せるなら同じことをするボタンが 2 つ並ぶだけなので外した。
 *
 * 押せることは**重ねたラベルで示す**。カーソルを乗せたときだけ出すと、乗せるまで
 * 分からない。常に出しておくと絵の邪魔になるので、角に小さく置いて、乗せたときに
 * はっきりさせる。
 *
 * **サムネイルがまだ無い図面でも、PDF があるなら押せる**。絵の生成は登録の
 * あとに走るので間に合っていないことがあり、そこで入口が消えると「PDF はあるのに
 * 開けない」状態になる。
 */
function Thumbnail({ src, onOpen }: { src: string | null; onOpen?: () => void }) {
  const frame =
    "flex aspect-4/3 w-full items-center justify-center overflow-hidden rounded-md border border-default-200 bg-default-50";

  if (!onOpen) {
    return (
      <div className={frame}>
        {src ? (
          <img src={src} alt="" className="size-full object-contain" />
        ) : (
          <span className="text-sm text-default-400">PDF がありません</span>
        )}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label="PDF を開く"
      className={`group relative cursor-pointer transition-shadow hover:border-brand-400 hover:shadow-md ${frame}`}
    >
      {src ? (
        <img src={src} alt="" className="size-full object-contain" />
      ) : (
        <span className="inline-flex items-center gap-1 text-sm text-brand-500">
          <ExternalLink size={14} />
          PDF を開く（サムネイルはまだありません）
        </span>
      )}
      {/* 右上に置く。図面は**右下が表題欄**（図番が書いてある場所）で、そこに
          重ねると一番読みたいところが隠れる。 */}
      {src && (
        <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded bg-default-900/60 px-2 py-1 text-tiny text-white opacity-70 transition-opacity group-hover:opacity-100">
          <ExternalLink size={12} />
          PDF を開く
        </span>
      )}
    </button>
  );
}

/**
 * 現場へ出す QR の押印。
 *
 * 押すと **QR 付きの版が 1 つ増える**。現場へ出す紙は QR 付きのほうなので、
 * 以後は一覧の絵も「PDF を開く」も QR 版を指す。
 *
 * **明細に紐付いていないと押せない**。QR は「どの案件のどの明細のどの図面か」を
 * 指すもので、紐付いていない図面に押すと、読み取っても指す先が無い紙になる。
 * ボタンを消さずに無効にして理由を出すのは、押せない理由が分からないと、
 * 何を直せばよいかも分からないため。
 *
 * **空白に置けなかったことは必ず伝える**。QR は余白のいちばん広い場所へ自動で
 * 置くが、線で埋まった図面では既存の線に重ねるしかない。紙を見るまで気づけない
 * 事故なので、その場で警告する。
 */
function QrSection({
  drawing,
  canWrite,
  onStampQr,
  onChanged,
  onError,
}: {
  drawing: PanelDrawing;
  canWrite: boolean;
  onStampQr: (
    drawingId: number,
    area: QrStampArea,
    scalePercent: number,
  ) => Promise<DrawingQrStamp>;
  /** 版が増えたときに呼ぶ（一覧のサムネイル・版数を引き直すため）。 */
  onChanged: () => void;
  onError: (message: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [choosingArea, setChoosingArea] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);
  const linked = drawing.item_id != null;

  const stamp = async (area: QrStampArea, scalePercent: number) => {
    setBusy(true);
    setWarning(null);
    onError(null);
    try {
      const result = await onStampQr(drawing.drawing_id, area, scalePercent);
      setChoosingArea(false);
      // 版が増えた（差し替えた）ので、この図面の版の履歴も引き直す。
      await queryClient.invalidateQueries({
        queryKey: ["drawing-revisions", drawing.drawing_id],
      });
      onChanged();
      showSuccess(`版 ${result.revision} として QR を押印しました。`);
      if (result.fallback_pages > 0) {
        setWarning(
          `${result.fallback_pages} ページは空白が取れず、図面の線に重ねて押しました。` +
            `印刷する前に PDF を確かめてください。`,
        );
      }
    } catch (e) {
      onError(errorMessage(e, "QR を押印できませんでした。"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 border-t border-default-200 pt-4">
      <div className="flex items-baseline gap-2">
        <h3 className="text-sm font-bold text-default-700">現場へ出す QR</h3>
        <span className="text-tiny text-default-500">
          押すと QR 付きの版が 1 つ増えます
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {canWrite && (
          <Button
            size="sm"
            radius="full"
            variant="bordered"
            color="primary"
            data-testid="drawing-stamp-qr"
            startContent={<QrCode size={14} />}
            isDisabled={!linked || busy}
            isLoading={busy}
            onPress={() => setChoosingArea(true)}
          >
            {drawing.qr_stamped ? "QR を押し直す" : "QR を押印する"}
          </Button>
        )}
        {drawing.qr_stamped && (
          <Chip size="sm" variant="flat" color="success">
            QR 押印済み
          </Chip>
        )}
      </div>

      {!linked ? (
        <p className="text-tiny text-default-500">
          明細に紐付けると押せます。QR は「どの案件のどの明細のどの図面か」を指すので、
          紐付いていないと読み取っても行き先がありません。
        </p>
      ) : (
        <p className="text-tiny text-default-500">
          押す前に紙のどこへ入れるかを選べます。選んだ区画の中で、余白のいちばん広い
          場所に入ります。読み取ると、この案件・明細・図面・版が分かります。
        </p>
      )}

      {warning && <p className="text-tiny text-warning-600">{warning}</p>}

      <QrAreaDialog
        isOpen={choosingArea}
        drawing={drawing}
        targetCount={1}
        isBusy={busy}
        onClose={() => setChoosingArea(false)}
        onConfirm={(area, scalePercent) => void stamp(area, scalePercent)}
      />
    </div>
  );
}

/**
 * 版（図面 PDF）の差し替えと履歴。
 *
 * 落としたらその場で上げる（「選ぶ → 送信」の 2 段にしない）。差し替えは 1 回の
 * 操作で終わってほしく、ダイアログの中にもう 1 段ボタンを置くと手数が増えるだけ。
 *
 * 一覧は**新しい版が上**。見たいのはたいてい最新で、古い版は「いつ差し替えたか」
 * を確かめるときに下まで辿る。
 */
function RevisionSection({
  drawingId,
  canWrite,
  onChanged,
  onError,
}: {
  drawingId: number;
  canWrite: boolean;
  /** 版が増減したときに呼ぶ（一覧のサムネイル・版数を引き直すため）。 */
  onChanged: () => void;
  onError: (message: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const revisions = useQuery({
    queryKey: ["drawing-revisions", drawingId],
    queryFn: () => listDrawingRevisions(drawingId),
  });
  const items = revisions.data?.items ?? [];

  const upload = async (file: File | null) => {
    if (!file) return;
    setBusy(true);
    onError(null);
    try {
      // 版は渡さない。サーバーが「登録済みの数字の版の最大 + 1」で採番する。
      const created = await uploadDrawingRevision(drawingId, file);
      await revisions.refetch();
      // サムネイルと図番の読み取りは応答のあとに走るので、一覧も引き直す
      // （出来上がりは図面タブの知らせ（SSE）で追いつく）。
      onChanged();
      showSuccess(`版 ${created.revision} として登録しました。`);
    } catch (e) {
      onError(errorMessage(e, "PDF を登録できませんでした。"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (revisionId: number, revision: string) => {
    setBusy(true);
    onError(null);
    try {
      await deleteDrawingRevision(drawingId, revisionId);
      await revisions.refetch();
      onChanged();
      showSuccess(`版 ${revision} を削除しました。`);
    } catch (e) {
      onError(errorMessage(e, "PDF を削除できませんでした。"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 border-t border-default-200 pt-4">
      <div className="flex items-baseline gap-2">
        <h3 className="text-sm font-bold text-default-700">図面PDFの版</h3>
        <span className="text-tiny text-default-500">
          {items.length} 件。差し替えると版が 1 つ増え、前の版は残ります
        </span>
      </div>

      {canWrite && (
        <FileDropZone
          label="差し替える図面PDF"
          accept=".pdf"
          showLabel={false}
          hint="落とすとそのまま新しい版として登録します（版番号は自動で付きます）。"
          file={null}
          isDisabled={busy}
          onSelect={(file) => void upload(file)}
        />
      )}

      {revisions.isPending ? (
        <div className="flex justify-center py-4">
          <Spinner size="sm" label="読み込み中..." />
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-default-500">まだ PDF が登録されていません。</p>
      ) : (
        <ul className="flex flex-col divide-y divide-default-100">
          {items.map((r) => (
            <li
              key={r.drawing_revision_id}
              className="flex items-center gap-3 py-2 text-sm"
            >
              <span className="w-14 shrink-0 font-bold">版 {r.revision}</span>
              {/* 版表記では QR 版と分からない（客先の版表記と同じ採番を使う）。 */}
              {r.has_qr && (
                <Chip size="sm" variant="flat" color="success" className="shrink-0">
                  QR
                </Chip>
              )}
              <button
                type="button"
                onClick={() =>
                  void openDrawingRevision(drawingId, r.drawing_revision_id)
                }
                aria-label={`版 ${r.revision} を開く`}
                className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 text-left text-brand-500 hover:text-brand-600"
              >
                <FileText size={14} className="shrink-0" />
                <span className="truncate underline underline-offset-2">
                  {r.original_filename ?? "図面.pdf"}
                </span>
              </button>
              <span className="shrink-0 text-tiny text-default-400">
                {r.created_at?.slice(0, 10)}
              </span>
              {canWrite && (
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  color="danger"
                  aria-label={`版 ${r.revision} を削除`}
                  isDisabled={busy}
                  onPress={() => void remove(r.drawing_revision_id, r.revision)}
                >
                  <Trash2 size={16} />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
